import { isStoreHuman } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { extractPromptTextFromAdf } from '../../common/utils/content-features/extractPromptTextFromAdf';
import type { ChatStoreAction } from './types';

/** Adds only a confirmed, unredacted first prompt to an already activated history snapshot. */
export const cacheConfirmedOpeningPrompt =
	({
		requestToken,
		conversationId,
		tmpHumanMessageId,
	}: {
		requestToken: object | undefined;
		conversationId: string;
		tmpHumanMessageId: string;
	}): ChatStoreAction =>
	({ getState, setState }, { getMessages }) => {
		const { promptHistory, conversations } = getState();
		const conversation = conversations[conversationId];
		if (
			!promptHistory ||
			!requestToken ||
			promptHistory.requestToken !== requestToken ||
			!conversation
		) {
			return;
		}
		const sessionId = conversation.backendConversationId ?? conversationId;
		if (promptHistory.removedSessionIds?.some((id) => id === conversationId || id === sessionId)) {
			return;
		}
		const message = getMessages(conversationId).find(
			(item) => isStoreHuman(item) && item.promptContextMessageId === tmpHumanMessageId,
		);
		if (
			!message ||
			!isStoreHuman(message) ||
			message.content.id === tmpHumanMessageId ||
			!message.content.id ||
			message.redactedPrompt !== undefined ||
			message.redactedByBlock ||
			message.content.isFromSeededConversation
		) {
			return;
		}
		const displayText = (
			typeof message.content.content === 'string'
				? message.content.content
				: extractPromptTextFromAdf(message.content.content)
		).trim();
		const createdAt = Date.parse(message.content.time_created);
		if (!displayText || !Number.isFinite(createdAt)) {
			return;
		}
		const item = {
			messageId: message.content.id,
			sessionId,
			displayText,
			truncated: false,
			isOpeningPrompt: true,
			createdAt,
		};
		setState({
			promptHistory: {
				...promptHistory,
				hasLoaded: true,
				items: [
					...promptHistory.items.filter(({ messageId }) => messageId !== item.messageId),
					item,
				].sort((a, b) => b.createdAt - a.createdAt),
			},
		});
	};
